"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { COLORS } from "@/lib/constants";

/** The WebGPU code (and vgpu) only downloads once the footer is close to the viewport. */
const ShapeWaves = dynamic(() => import("@/components/ui/ShapeWaves"), { ssr: false });

/** Phones (below Tailwind's md breakpoint) get the plain footer: the waves read as noise at that size. */
const WIDE = "(min-width: 768px)";
const subscribeWide = (onChange: () => void) => {
  const mq = window.matchMedia(WIDE);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
};
const useWide = () => useSyncExternalStore(subscribeWide, () => window.matchMedia(WIDE).matches, () => false);

/**
 * React Bits' Shape Waves behind the footer: platinum shapes on white that roll past and
 * brighten to mint under the pointer. Kept faint and masked away from the address block so
 * the text stays readable.
 *
 * Nothing loads until the footer is within a screen of the viewport, and browsers without
 * WebGPU, or narrower than md, never load it at all; the footer is plain white for them. ShapeWaves
 * itself pauses off screen, in hidden tabs and under prefers-reduced-motion.
 */
export const FooterWaves = () => {
  const ref = useRef<HTMLDivElement>(null);
  const wide = useWide();
  const [near, setNear] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!wide || !el || !("gpu" in navigator)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setNear(true);
        observer.disconnect();
      },
      { rootMargin: "100% 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [wide]);

  return (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden [mask-image:linear-gradient(to_bottom,black,black_45%,transparent_85%)]"
    >
      {wide && near && (
        <ShapeWaves
          shapes="mixed"
          backgroundColor={COLORS.WHITE}
          color={COLORS.PLATINUM}
          hoverColor={COLORS.MINT}
          cellSize={14}
          brightness={0.55}
          contrast={1.2}
          glow={0.3}
          speed={0.6}
          intro={false}
          onError={() => setNear(false)}
        />
      )}
    </div>
  );
};
